import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { useState, useEffect, useContext, useMemo } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import {
  Search,
  RotateCcw,
  Plus,
  Package,
  Layers,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const SupplierWithItems = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");

  const [items, setItems] = useState([]);
  const [existingItems, setExistingItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Items to Add pagination
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsTotalPages, setItemsTotalPages] = useState(1);
  const [itemsTotalRecords, setItemsTotalRecords] = useState(0);

  // Existing Items pagination
  const [existingPage, setExistingPage] = useState(1);
  const [existingTotalPages, setExistingTotalPages] = useState(1);
  const [existingTotalRecords, setExistingTotalRecords] = useState(0);

  const [limit] = useState(10);

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const getBranches = async () => {
    try {
      const res = await axios.get("https://nissi-india.com/api/getAllBranches", {
        headers: commonHeaders,
      });
      const branchList = Array.isArray(res.data.branches) ? res.data.branches : [];
      setBranches(branchList);

      // Auto-select login branch on page load
      if (branchId && branchList.some((b) => String(b.fld_branchid) === String(branchId))) {
        setSelectedBranch(branchId);
      }
    } catch (err) {
      console.error("Failed to load branches:", err);
      toast.error("Failed to load branches");
    }
  };

  const getCategories = async () => {
    try {
      const res = await axios.get("https://nissi-india.com/api/branchItems_cat", {
        headers: commonHeaders,
      });
      setCategories(Array.isArray(res.data.cat) ? res.data.cat : []);
    } catch (err) {
      console.error("Failed to load categories:", err);
      toast.error("Failed to load categories");
    }
  };

  const getSuppliers = async (branchIdValue) => {
    try {
      const res = await axios.get(
        `https://nissi-india.com/api/supplierListByBranch?branchid=${branchIdValue}`,
        { headers: commonHeaders }
      );
      setSuppliers(Array.isArray(res.data?.supplierList) ? res.data.supplierList : []);
    } catch (err) {
      console.error("Failed to fetch suppliers:", err);
      toast.error("Failed to fetch suppliers");
      setSuppliers([]);
    }
  };

  const fetchItems = async (
    page = itemsPage,
    supplierIdValue = selectedSupplier,
    categoryIdValue = selectedCategory,
    itemNameValue = itemName,
    itemCodeValue = itemCode
  ) => {
    if (!selectedBranch) return;

    try {
      const res = await axios.get(
        `https://nissi-india.com/api/supplierWithItems?supplierid=${supplierIdValue || ""}&categoryid=${categoryIdValue || ""}&itemname=${encodeURIComponent(
          itemNameValue || ""
        )}&itemcode=${encodeURIComponent(itemCodeValue || "")}&branchid=${selectedBranch}&page=${page}&limit=${limit}`,
        { headers: commonHeaders }
      );

      const { availableItems = [], totalRecords = 0, totalPages = 1 } = res.data || {};

      setItems(Array.isArray(availableItems) ? availableItems : []);
      setItemsTotalRecords(totalRecords);
      setItemsTotalPages(totalPages || 1);
    } catch (err) {
      console.error("Failed to load supplier items:", err);
      toast.error("Failed to load supplier items");
      setItems([]);
      setItemsTotalRecords(0);
      setItemsTotalPages(1);
    }
  };

  const fetchExistingItems = async (
    page = existingPage,
    supplierIdValue = selectedSupplier,
    categoryIdValue = selectedCategory,
    itemNameValue = itemName,
    itemCodeValue = itemCode
  ) => {
    if (!selectedBranch) return;

    try {
      const res = await axios.get(
        `https://nissi-india.com/api/supplierWithExistingItems?supplierid=${supplierIdValue || ""}&categoryid=${categoryIdValue || ""}&itemname=${encodeURIComponent(
          itemNameValue || ""
        )}&itemcode=${encodeURIComponent(itemCodeValue || "")}&branchid=${selectedBranch}&page=${page}&limit=${limit}`,
        { headers: commonHeaders }
      );

      const { itemList = [], totalRecords = 0, totalPages = 1 } = res.data || {};

      setExistingItems(Array.isArray(itemList) ? itemList : []);
      setExistingTotalRecords(totalRecords);
      setExistingTotalPages(totalPages || 1);
    } catch (err) {
      console.error("Failed to load existing items:", err);
      toast.error("Failed to load existing items");
      setExistingItems([]);
      setExistingTotalRecords(0);
      setExistingTotalPages(1);
    }
  };

  const handleAddItems = async () => {
    const selectedItems = items.filter((i) => i.select);

    if (selectedItems.length === 0) {
      return toast.warning("No items selected to add");
    }

    if (!selectedSupplier) {
      return toast.warning("Please select a supplier first");
    }

    // FIX: guard in case a negative value slipped past the onChange filter.
    const hasNegative = selectedItems.some((item) =>
      ["price", "vat", "cst", "ed", "ds", "sc", "pf"].some(
        (field) => Number(item[field]) < 0
      )
    );
    if (hasNegative) {
      return toast.error("GST/tax fields and price cannot be negative.");
    }

    try {
      setSubmitting(true);
      await axios.post(
        "https://nissi-india.com/api/addSupplierItems",
        {
          supplierid: selectedSupplier,
          loginuserid: userId || 1,
          itemList: selectedItems.map((item) => ({
            itemid: item.itemid,
            price: item.price || 0,
            vat: item.vat || 0,
            cst: item.cst || 0,
            ed: item.ed || 0,
            ds: item.ds || 0,
            sc: item.sc || 0,
            pf: item.pf || 0,
          })),
        },
        { headers: commonHeaders }
      );

      toast.success("Items added successfully");
      setItemsPage(1);
      setExistingPage(1);
      fetchItems(1);
      fetchExistingItems(1);
    } catch (err) {
      console.error("Failed to add items:", err);
      toast.error("Failed to add items");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearch = () => {
    if (!selectedBranch) {
      toast.warning("Please select a branch");
      return;
    }
    setItemsPage(1);
    setExistingPage(1);
    fetchItems(1);
    fetchExistingItems(1);
  };

  const handleReset = () => {
    setSelectedSupplier("");
    setSelectedCategory("");
    setItemCode("");
    setItemName("");
    setItemsPage(1);
    setExistingPage(1);
    fetchItems(1, "", "", "", "");
    fetchExistingItems(1, "", "", "", "");
  };

  useEffect(() => {
    getBranches();
    getCategories();
  }, [commonHeaders]);

  useEffect(() => {
    if (selectedBranch) {
      getSuppliers(selectedBranch);
      setItemsPage(1);
      setExistingPage(1);
      fetchItems(1);
      fetchExistingItems(1);
    } else {
      setSuppliers([]);
      setSelectedSupplier("");
      setItems([]);
      setExistingItems([]);
      setItemsTotalPages(1);
      setItemsTotalRecords(0);
      setExistingTotalPages(1);
      setExistingTotalRecords(0);
    }
  }, [selectedBranch, commonHeaders]);

  useEffect(() => {
    if (selectedBranch) {
      fetchItems(itemsPage);
    }
  }, [itemsPage]);

  useEffect(() => {
    if (selectedBranch) {
      fetchExistingItems(existingPage);
    }
  }, [existingPage]);

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Supplier With Items</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header + Filters Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Supplier Item Configuration</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Link items to suppliers with pricing and tax details
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
                        <div>
              <label className={labelClass}>Branch</label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className={inputClass}
              >
                <option value="">Select Branch</option>
                {branches.map((b) => (
                  <option key={b.fld_branchid} value={b.fld_branchid}>
                    {b.fld_Branchname}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Supplier</label>
              <select
                value={selectedSupplier}
                onChange={(e) => setSelectedSupplier(e.target.value)}
                className={inputClass}
              >
                <option value="">Select Supplier</option>
                {suppliers.map((s) => (
                  <option key={s.fld_supplierid} value={s.fld_supplierid}>
                    {s.fld_suppliername}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={inputClass}
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c.fld_itemcategoryid} value={c.fld_itemcategoryid}>
                    {c.fld_itemcategory_name || "Unnamed"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Item Code</label>
              <input
                type="text"
                value={itemCode}
                onChange={(e) => setItemCode(e.target.value)}
                placeholder="Item Code"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Item Name</label>
              <input
                type="text"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                placeholder="Item Name"
                className={inputClass}
              />
            </div>

                        <div className="flex items-end gap-2 sm:col-span-2 xl:col-span-2">
              <button
                onClick={handleSearch}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <Search size={16} />
                Search
              </button>
              <button
                onClick={handleReset}
                title="Clear filters"
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-200 hover:bg-slate-700/50"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Items to Add Table */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Items to Add</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {itemsTotalRecords} items
                </span>
              </div>

              <button
                onClick={handleAddItems}
                disabled={submitting || items.filter((i) => i.select).length === 0}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Plus size={16} />
                {submitting ? "Adding..." : "Add Selected Items"}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase">Item Code</th>
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase">Item Name</th>
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase">Part No</th>
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase">UOM</th>
                            <th className="px-3 py-3 text-left text-xs font-bold uppercase">Price</th>
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase">CST</th>
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase">ED</th>
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase">DS</th>
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase">SC</th>
                  <th className="px-3 py-3 text-left text-xs font-bold uppercase">P&F</th>
                  <th className="px-3 py-3 text-center text-xs font-bold uppercase">Select</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {items.length > 0 ? (
                  items.map((item, idx) => (
                    <tr
                      key={item.itemid || idx}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-3 py-3 font-medium">
                        {(itemsPage - 1) * limit + idx + 1}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-200"
                              : `bg-${baseColor}-100 text-${baseColor}-800`
                          }`}
                        >
                          {item.itemcode}
                        </span>
                      </td>
                      <td className="px-3 py-3 font-medium">{item.itemname}</td>
                      <td className="px-3 py-3">{item.fld_itemPartNo || "-"}</td>
                      <td className="px-3 py-3">{item.itemuom}</td>

                      {/* FIX: VAT column removed — not required on this page */}
                      {["price", "cst", "ed", "ds", "sc", "pf"].map((field) => (
                                                <td className="px-3 py-3" key={field}>
                          <input
                            type="number"
                            min="0"
                            className={`w-16 px-2 py-1 rounded border text-sm ${
                              isDark
                                ? "bg-slate-800 border-slate-600 text-white"
                                : "bg-white border-slate-300"
                            }`}
                            value={item[field] || ""}
                            onChange={(e) => {
                              // FIX: GST/tax fields (VAT, CST, ED, DS, SC, P&F)
                              // accepted negative values with no validation.
                              const val = e.target.value;
                              if (val !== "" && Number(val) < 0) return;
                              setItems((prev) =>
                                prev.map((it) =>
                                  it.itemid === item.itemid
                                    ? { ...it, [field]: val }
                                    : it
                                )
                              );
                            }}
                          />
                        </td>
                      ))}

                      <td className="px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={!!item.select}
                          onChange={() =>
                            setItems((prev) =>
                              prev.map((it) =>
                                it.itemid === item.itemid
                                  ? { ...it, select: !it.select }
                                  : it
                              )
                            )
                          }
                          className="w-4 h-4 rounded"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={12} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Package size={40} className="text-gray-400" />
                        <span>No items found. Select filters and search.</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Items to Add Pagination */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Showing{" "}
              <span className="font-semibold">
                {itemsTotalRecords === 0 ? 0 : (itemsPage - 1) * limit + 1}
              </span>{" "}
              to{" "}
              <span className="font-semibold">
                {Math.min(itemsPage * limit, itemsTotalRecords)}
              </span>{" "}
              of <span className="font-semibold">{itemsTotalRecords}</span> results
            </div>

            <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
              <button
                onClick={() => setItemsPage((p) => Math.max(p - 1, 1))}
                disabled={itemsPage === 1}
                className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                  itemsPage === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                <ChevronLeft size={16} />
                Prev
              </button>

              {[...Array(Math.min(itemsTotalPages, 5))].map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setItemsPage(idx + 1)}
                  className={`px-4 py-2 text-sm font-medium transition ${
                    itemsPage === idx + 1
                      ? `bg-${baseColor}-${baseShade} text-white`
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  {idx + 1}
                </button>
              ))}

              <button
                onClick={() => setItemsPage((p) => Math.min(p + 1, itemsTotalPages))}
                disabled={itemsPage === itemsTotalPages || itemsTotalPages === 0}
                className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                  itemsPage === itemsTotalPages || itemsTotalPages === 0
                    ? "text-gray-400 cursor-not-allowed"
                    : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Existing Items Table */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Existing Supplier Items</h3>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                }`}
              >
                {existingTotalRecords} items
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">CST</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">ED</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">DS</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">SC</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">P&F</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {existingItems.length > 0 ? (
                  existingItems.map((item, idx) => (
                    <tr
                      key={item.fld_supplieritemid || idx}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">
                        {(existingPage - 1) * limit + idx + 1}
                      </td>
                      <td className="px-4 py-3 font-medium">{item.itemname || item.fld_itemname}</td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-green-600">
                          ₹{parseFloat(item.price || item.fld_price || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3">{item.cst || item.fld_CST || 0}</td>
                      <td className="px-4 py-3">{item.ed || item.fld_ED || 0}</td>
                      <td className="px-4 py-3">{item.ds || item.fld_DS || 0}</td>
                      <td className="px-4 py-3">{item.sc || item.fld_SC || 0}</td>
                      <td className="px-4 py-3">{item.pf || item.fld_PandF || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Layers size={40} className="text-gray-400" />
                        <span>No existing items found for this supplier</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Existing Items Pagination */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Showing{" "}
              <span className="font-semibold">
                {existingTotalRecords === 0 ? 0 : (existingPage - 1) * limit + 1}
              </span>{" "}
              to{" "}
              <span className="font-semibold">
                {Math.min(existingPage * limit, existingTotalRecords)}
              </span>{" "}
              of <span className="font-semibold">{existingTotalRecords}</span> results
            </div>

            <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
              <button
                onClick={() => setExistingPage((p) => Math.max(p - 1, 1))}
                disabled={existingPage === 1}
                className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                  existingPage === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                <ChevronLeft size={16} />
                Prev
              </button>

              {[...Array(Math.min(existingTotalPages, 5))].map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setExistingPage(idx + 1)}
                  className={`px-4 py-2 text-sm font-medium transition ${
                    existingPage === idx + 1
                      ? `bg-${baseColor}-${baseShade} text-white`
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  {idx + 1}
                </button>
              ))}

              <button
                onClick={() => setExistingPage((p) => Math.min(p + 1, existingTotalPages))}
                disabled={existingPage === existingTotalPages || existingTotalPages === 0}
                className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                  existingPage === existingTotalPages || existingTotalPages === 0
                    ? "text-gray-400 cursor-not-allowed"
                    : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default SupplierWithItems;